import proj4 from 'proj4';

// Register coordinate system definitions once at module load
proj4.defs(
  'EPSG:27700',
  '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000' +
    ' +ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs'
);
proj4.defs(
  'EPSG:29900',
  '+proj=tmerc +lat_0=53.5 +lon_0=-8 +k=1.000035 +x_0=200000 +y_0=250000' +
    ' +a=6377340.189 +b=6356034.447938534 +towgs84=482.5,-130.6,564.6,-1.042,-0.214,-0.631,8.15 +units=m +no_defs'
);
// NZ Map Grid. Params copied verbatim from the mobile apps (iOS `GridRef.toProjection`,
// Android `GridRef.toProjection`) so a numeric pair resolves to the same point on
// every platform. Note this is NZMG, *not* NZTM2000 (EPSG:2193).
proj4.defs(
  'EPSG:27200',
  '+proj=nzmg +lat_0=-41 +lon_0=173 +x_0=2510000 +y_0=6023150' +
    ' +ellps=intl +towgs84=59.47,-5.04,187.44,0.47,-0.1,1.024,-4.5993 +units=m +no_defs'
);

export interface ParsedCoordinate {
  lat: number;
  lng: number;
  displayName: string;
  format: string;
}

/** A projected grid that can be addressed by a bare easting/northing pair. */
interface NumericGridSystem {
  epsg: string;
  format: string;
  minEasting: number;
  maxEasting: number;
  minNorthing: number;
  maxNorthing: number;
}

/**
 * Grids that accept an all-numeric easting/northing pair, with the bounds of
 * each grid's defined extent.
 *
 * BNG and Irish Grid overlap (Irish sits wholly inside the BNG box), so a pair
 * such as `216692, 271277` is a legitimate position in *both* — Cardigan Bay vs
 * Co. Longford, ~250 km apart. Nothing in the input can break that tie, so
 * `numericGridCandidates` returns every grid that accepts the pair and lets the
 * user choose. NZMG doesn't overlap either (its extent starts near E 1.9M).
 */
const NUMERIC_GRID_SYSTEMS: NumericGridSystem[] = [
  {
    epsg: 'EPSG:27700',
    format: 'OS Grid (BNG)',
    minEasting: 0,
    maxEasting: 700000,
    minNorthing: 0,
    maxNorthing: 1300000,
  },
  {
    epsg: 'EPSG:29900',
    format: 'Irish Grid',
    minEasting: 0,
    maxEasting: 500000,
    minNorthing: 0,
    maxNorthing: 500000,
  },
  {
    epsg: 'EPSG:27200',
    format: 'NZ Grid',
    // Matches the bounds the mobile apps already apply (Android
    // `GridReferenceParser.tryNzGrid`, iOS `GridRef.numericExtent`).
    minEasting: 1950000,
    maxEasting: 3000000,
    minNorthing: 5300000,
    maxNorthing: 6800000,
  },
];

function isValidLatLng(lat: number, lng: number): boolean {
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/** Project an easting/northing in `epsg` to WGS84, or null if it doesn't land on Earth. */
function projectToLatLng(
  epsg: string,
  easting: number,
  northing: number
): { lat: number; lng: number } | null {
  try {
    const [lng, lat] = proj4(epsg, 'EPSG:4326', [easting, northing]);
    return isValidLatLng(lat, lng) ? { lat, lng } : null;
  } catch {
    return null;
  }
}

/**
 * Map a grid letter (A–Z, skipping I) to a 0–24 value.
 * A=0 … H=7, J=8 … Z=24
 */
function letterVal(ch: string): number {
  const code = ch.toUpperCase().charCodeAt(0);
  return code > 73 ? code - 66 : code - 65; // 73 = 'I'
}

/**
 * Convert a British National Grid reference string to WGS84.
 * Accepts cleaned refs like "SU12345678" (even digit count 4–10).
 */
function bngToLatLng(ref: string): { lat: number; lng: number } | null {
  const clean = ref.toUpperCase().replace(/\s+/g, '');
  const m = clean.match(/^([A-HJ-Z]{2})(\d{4,10})$/);
  if (!m || m[2].length % 2 !== 0) return null;

  const l1 = letterVal(m[1][0]);
  const l2 = letterVal(m[1][1]);
  const digits = m[2];
  const half = digits.length / 2;
  const scale = Math.pow(10, 5 - half);

  // Each 500 km macro-square origin, offset to OSGB36 false origin
  const easting =
    (l1 % 5) * 500000 + (l2 % 5) * 100000 - 1000000 +
    parseInt(digits.slice(0, half), 10) * scale;
  const northing =
    (4 - Math.floor(l1 / 5)) * 500000 + (4 - Math.floor(l2 / 5)) * 100000 - 500000 +
    parseInt(digits.slice(half), 10) * scale;

  return projectToLatLng('EPSG:27700', easting, northing);
}

/**
 * Convert an Irish National Grid reference string to WGS84.
 * Accepts cleaned refs like "O15993427" (single letter + even digit count 4–10).
 */
function irishGridToLatLng(ref: string): { lat: number; lng: number } | null {
  const clean = ref.toUpperCase().replace(/\s+/g, '');
  const m = clean.match(/^([A-HJ-Z])(\d{4,10})$/);
  if (!m || m[2].length % 2 !== 0) return null;

  const lv = letterVal(m[1]);
  const digits = m[2];
  const half = digits.length / 2;
  const scale = Math.pow(10, 5 - half);

  const easting = (lv % 5) * 100000 + parseInt(digits.slice(0, half), 10) * scale;
  const northing = (4 - Math.floor(lv / 5)) * 100000 + parseInt(digits.slice(half), 10) * scale;

  return projectToLatLng('EPSG:29900', easting, northing);
}

export interface EastingNorthing {
  easting: number;
  northing: number;
}

/**
 * An all-numeric easting/northing pair, as pasted from a GPS unit, a GIS export
 * or an emergency-services callout. Accepts comma, comma-space or space between
 * the two numbers, optional sub-metre decimals, and optional `E`/`Easting`
 * `N`/`Northing` labels.
 *
 * **Both numbers must have at least 4 integer digits.** That single rule is what
 * keeps this grammar disjoint from a lat/lng pair rather than merely unlikely to
 * collide with one: |lat| <= 90 and |lng| <= 180, so a decimal-degrees value can
 * never exceed 3 integer digits, and every DD regex in this file is capped at
 * `\d{1,3}`. A 4-digit floor therefore cannot swallow `56.79, -5.00`, and it
 * still admits the westernmost GB land (St Kilda sits at E 9655). No sign is
 * accepted either, since grid eastings/northings are unsigned within their
 * extent — so a negative longitude can't reach this branch at all.
 *
 * Values are literal metres. They are NOT zero-padded the way a truncated
 * lettered ref is scaled: `216692` means 216692 m east, never 2166920.
 */
export function parseEastingNorthingPair(input: string): EastingNorthing | null {
  const m = input
    .trim()
    .match(
      /^(?:E(?:asting)?\s*:?\s+)?(\d{4,7}(?:\.\d+)?)\s*(?:,\s*|\s+)(?:N(?:orthing)?\s*:?\s+)?(\d{4,7}(?:\.\d+)?)$/i
    );
  if (!m) return null;

  return { easting: parseFloat(m[1]), northing: parseFloat(m[2]) };
}

/**
 * Every grid in which a bare easting/northing pair is a valid position, in
 * `NUMERIC_GRID_SYSTEMS` order. Returns more than one entry when the pair falls
 * inside overlapping grids (BNG and Irish Grid) — see `NUMERIC_GRID_SYSTEMS`.
 */
export function numericGridCandidates(input: string): ParsedCoordinate[] {
  const pair = parseEastingNorthingPair(input);
  if (!pair) return [];

  const candidates: ParsedCoordinate[] = [];
  for (const grid of NUMERIC_GRID_SYSTEMS) {
    if (pair.easting < grid.minEasting || pair.easting > grid.maxEasting) continue;
    if (pair.northing < grid.minNorthing || pair.northing > grid.maxNorthing) continue;

    const pos = projectToLatLng(grid.epsg, pair.easting, pair.northing);
    if (!pos) continue;

    // Name the row by the resolved position, not the typed numbers — when a pair
    // is valid in two grids the numbers are identical and only the place differs.
    candidates.push({
      ...pos,
      displayName: formatLatLng(pos.lat, pos.lng),
      format: grid.format,
    });
  }
  return candidates;
}

function applyHemisphere(val: number, dir: string): number {
  const d = dir.toUpperCase();
  return d === 'S' || d === 'W' ? -Math.abs(val) : Math.abs(val);
}

function dmsToDecimal(deg: number, min: number, sec: number, dir: string): number {
  return applyHemisphere(deg + min / 60 + sec / 3600, dir);
}

function formatLatLng(lat: number, lng: number): string {
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(5)}°${latDir}, ${Math.abs(lng).toFixed(5)}°${lngDir}`;
}

/**
 * Parse any supported coordinate format into WGS84 lat/lng.
 *
 * Supported formats:
 *   - Decimal Degrees:  "53.1234, -6.5678"  |  "53.1234 -6.5678"
 *   - DD with NSEW:     "53.1234°N 6.5678°W"  |  "N53.1234 W6.5678"
 *   - DMS:              "53°07'24\"N, 6°34'04\"W"
 *   - DDM:              "53°07.400'N, 6°34.070'W"
 *   - OS Grid (BNG):    "SU 123 456"  |  "SU12345678"
 *   - Irish Grid:       "O 159 342"   |  "O15993427"
 *   - Numeric E/N:      "216692, 771277"  |  "E 216692 N 771277"
 *
 * Every format but the last resolves to exactly one place, so the array holds a
 * single entry. A bare easting/northing pair can be valid in more than one grid
 * (see `numericGridCandidates`), which is the reason this returns a list.
 */
export function parseCoordinates(input: string): ParsedCoordinate[] {
  const s = input.trim();
  if (!s) return [];

  // ── DMS: 53°07'24"N, 6°34'04"W ──────────────────────────────────────────
  const dms = s.match(
    /^(\d{1,3})[°\s]\s*(\d{1,2})['\s]\s*(\d{1,2}(?:\.\d+)?)["\s]*([NS])\s*[,\s]\s*(\d{1,3})[°\s]\s*(\d{1,2})['\s]\s*(\d{1,2}(?:\.\d+)?)["\s]*([EW])$/i
  );
  if (dms) {
    const lat = dmsToDecimal(+dms[1], +dms[2], +dms[3], dms[4]);
    const lng = dmsToDecimal(+dms[5], +dms[6], +dms[7], dms[8]);
    if (isValidLatLng(lat, lng)) {
      return [{ lat, lng, displayName: formatLatLng(lat, lng), format: 'DMS' }];
    }
  }

  // ── DDM: 53°07.400'N, 6°34.070'W ────────────────────────────────────────
  const ddm = s.match(
    /^(\d{1,3})[°\s]\s*(\d{1,2}(?:\.\d+)?)['\s]*([NS])\s*[,\s]\s*(\d{1,3})[°\s]\s*(\d{1,2}(?:\.\d+)?)['\s]*([EW])$/i
  );
  if (ddm) {
    const lat = dmsToDecimal(+ddm[1], +ddm[2], 0, ddm[3]);
    const lng = dmsToDecimal(+ddm[4], +ddm[5], 0, ddm[6]);
    if (isValidLatLng(lat, lng)) {
      return [{ lat, lng, displayName: formatLatLng(lat, lng), format: 'DDM' }];
    }
  }

  // ── Decimal degrees with NSEW suffix: "53.1234°N 6.5678°W" ──────────────
  const ddNsew = s.match(
    /^(\d{1,2}(?:\.\d+)?)°?\s*([NS])\s*[,\s]\s*(\d{1,3}(?:\.\d+)?)°?\s*([EW])$/i
  );
  if (ddNsew) {
    const lat = applyHemisphere(+ddNsew[1], ddNsew[2]);
    const lng = applyHemisphere(+ddNsew[3], ddNsew[4]);
    if (isValidLatLng(lat, lng)) {
      return [{ lat, lng, displayName: formatLatLng(lat, lng), format: 'Decimal Degrees' }];
    }
  }

  // ── Decimal degrees with NSEW prefix: "N53.1234 W6.5678" ─────────────────
  const ddNsewPrefix = s.match(
    /^([NS])\s*(\d{1,2}(?:\.\d+)?)°?\s*[,\s]\s*([EW])\s*(\d{1,3}(?:\.\d+)?)°?$/i
  );
  if (ddNsewPrefix) {
    const lat = applyHemisphere(+ddNsewPrefix[2], ddNsewPrefix[1]);
    const lng = applyHemisphere(+ddNsewPrefix[4], ddNsewPrefix[3]);
    if (isValidLatLng(lat, lng)) {
      return [{ lat, lng, displayName: formatLatLng(lat, lng), format: 'Decimal Degrees' }];
    }
  }

  // ── Plain decimal degrees: "53.1234, -6.5678" or "53.1234 -6.5678" ──────
  const ddPlain = s.match(
    /^([-+]?\d{1,3}(?:\.\d+)?)\s*(?:,\s*|\s+)([-+]?\d{1,3}(?:\.\d+)?)$/
  );
  if (ddPlain) {
    const a = parseFloat(ddPlain[1]);
    const b = parseFloat(ddPlain[2]);
    // Resolve ambiguity: if one value exceeds ±90 it must be longitude
    let lat: number, lng: number;
    if (Math.abs(a) > 90 && Math.abs(b) <= 90) {
      lat = b; lng = a;
    } else if (Math.abs(b) > 90 && Math.abs(a) <= 90) {
      lat = a; lng = b;
    } else {
      // Both in lat range – assume conventional lat, lng order
      lat = a; lng = b;
    }
    if (isValidLatLng(lat, lng)) {
      return [{ lat, lng, displayName: formatLatLng(lat, lng), format: 'Decimal Degrees' }];
    }
  }

  // ── OS Grid (BNG): "SU 123 456" or "SU12345678" ──────────────────────────
  const bngSpaced = s.match(/^([A-HJ-Z]{2})\s+(\d{3,5})\s+(\d{3,5})$/i);
  if (bngSpaced) {
    const digits = bngSpaced[2] + bngSpaced[3];
    if (digits.length % 2 === 0) {
      const pos = bngToLatLng(bngSpaced[1] + digits);
      if (pos) {
        return [{
          ...pos,
          displayName: `${bngSpaced[1].toUpperCase()} ${bngSpaced[2]} ${bngSpaced[3]}`,
          format: 'OS Grid (BNG)',
        }];
      }
    }
  }

  const bngCompact = s.match(/^([A-HJ-Z]{2}\d{4,10})$/i);
  if (bngCompact) {
    const pos = bngToLatLng(bngCompact[1]);
    if (pos) {
      return [{
        ...pos,
        displayName: bngCompact[1].toUpperCase(),
        format: 'OS Grid (BNG)',
      }];
    }
  }

  // ── Irish National Grid: "O 159 342" or "O15993427" ──────────────────────
  const igSpaced = s.match(/^([A-HJ-Z])\s+(\d{3,5})\s+(\d{3,5})$/i);
  if (igSpaced) {
    const digits = igSpaced[2] + igSpaced[3];
    if (digits.length % 2 === 0) {
      const pos = irishGridToLatLng(igSpaced[1] + digits);
      if (pos) {
        return [{
          ...pos,
          displayName: `${igSpaced[1].toUpperCase()} ${igSpaced[2]} ${igSpaced[3]}`,
          format: 'Irish Grid',
        }];
      }
    }
  }

  const igCompact = s.match(/^([A-HJ-Z]\d{4,10})$/i);
  if (igCompact) {
    const pos = irishGridToLatLng(igCompact[1]);
    if (pos) {
      return [{
        ...pos,
        displayName: igCompact[1].toUpperCase(),
        format: 'Irish Grid',
      }];
    }
  }

  // ── Numeric easting/northing: "216692, 771277" | "E 216692 N 771277" ─────
  // Last, because it's the loosest grammar. It can't shadow the branches above:
  // they all require a letter, a degree/hemisphere marker, or <= 3 integer
  // digits, none of which this branch accepts.
  return numericGridCandidates(s);
}

/**
 * Single-result form of {@link parseCoordinates}, kept for call sites that only
 * ever want one place. Where a numeric pair is valid in several grids this
 * yields the first candidate — prefer `parseCoordinates` and show them all.
 */
export function parseCoordinate(input: string): ParsedCoordinate | null {
  return parseCoordinates(input)[0] ?? null;
}
